import React, { useEffect } from "react"
import "./Layout.css"
import AldooBoot from "aldoo-ra/Boot"
import Site from "../components/Site"
import { VariantProvider } from "aldoo-ra/VariantReader/VariantContext.jsx"
import { usePageContext } from "vike-react/usePageContext"

// Add project-specific local variant overrides here, e.g.:
// import TypographyVariants from "../components/Variants/Typography.jsx"
// const localVariants = { Typography: TypographyVariants }
const localVariants = {}

// Merge SSR/CMS variants with local file variants.
// Local variants fill in any empty or missing values from the server,
// so bad/incomplete CMS data never overrides correct local definitions.
function mergeWithLocalFallback(ssrVariants) {
  const result = {}
  const names = new Set([
    ...Object.keys(ssrVariants || {}),
    ...Object.keys(localVariants),
  ])
  for (const name of names) {
    const server = ssrVariants?.[name] || {}
    const local = localVariants[name] || {}
    const merged = { ...server }
    for (const [key, value] of Object.entries(local)) {
      if (!merged[key]) merged[key] = value
    }
    result[name] = merged
  }
  return result
}

const SCROLL_KEY = "aldoo_scroll_y"

export function Layout({ children }) {
  const pageContext = usePageContext()
  const variants = mergeWithLocalFallback(pageContext.ssrVariants)

  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual"
    }

    const saved = localStorage.getItem(SCROLL_KEY)
    if (saved !== null) {
      const target = parseInt(saved, 10)
      const deadline = Date.now() + 3000
      const tryScroll = () => {
        if (document.body.scrollHeight >= target + window.innerHeight) {
          window.scrollTo(0, target)
        } else if (Date.now() < deadline) {
          requestAnimationFrame(tryScroll)
        }
      }
      if (document.readyState === "complete") {
        tryScroll()
      } else {
        window.addEventListener("load", tryScroll, { once: true })
      }
    }

    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          localStorage.setItem(SCROLL_KEY, String(window.scrollY))
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <React.StrictMode>
      <VariantProvider ssrVariants={variants}>
        <AldooBoot />
        <Site>{children}</Site>
      </VariantProvider>
    </React.StrictMode>
  )
}
